import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { FiSend, FiX } from 'react-icons/fi';

const CreateNotificationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    type: 'info',
    title: '',
    message: '',
    role: null, // null = tous les rôles
    expires_at: ''
  });

  const queryClient = useQueryClient();

  const createNotificationMutation = useMutation(
    (data) => api.post('/notifications/global', data),
    {
      onSuccess: () => {
        toast.success('Notification créée avec succès !');
        queryClient.invalidateQueries('notifications');
        onClose();
        setFormData({
          type: 'info',
          title: '',
          message: '',
          role: null,
          expires_at: ''
        });
      },
      onError: (error) => {
        toast.error('Erreur lors de la création de la notification');
        console.error('Erreur:', error);
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('Le titre et le message sont requis');
      return;
    }

    createNotificationMutation.mutate({
      type: formData.type,
      title: formData.title.trim(),
      message: formData.message.trim(),
      role: formData.role || null,
      expires_at: formData.expires_at || null
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-80 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Créer une notification globale
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Type de notification */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Type de notification
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="info">ℹ️ Information</option>
              <option value="success">✅ Succès</option>
              <option value="warning">⚠️ Avertissement</option>
              <option value="error">❌ Erreur</option>
            </select>
          </div>

          {/* Rôle cible */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Destinataires
            </label>
            <select
              value={formData.role || ''}
              onChange={(e) => handleChange('role', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tous les rôles</option>
              <option value="admin">Administrateurs</option>
              <option value="secretaire">Secrétaires</option>
              <option value="intendant">Intendants</option>
              <option value="enseignant">Enseignants</option>
            </select>
          </div>

          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Titre *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Titre de la notification"
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Message *
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Contenu de la notification"
              required
            />
          </div>

          {/* Date d'expiration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Expiration (optionnel)
            </label>
            <input
              type="datetime-local"
              value={formData.expires_at}
              onChange={(e) => handleChange('expires_at', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={createNotificationMutation.isLoading}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors flex items-center"
            >
              {createNotificationMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Création...
                </>
              ) : (
                <>
                  <FiSend className="h-4 w-4 mr-2" />
                  Envoyer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateNotificationModal;
